import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";

import {
    Modal,
    Grid,
    Button,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";

import { CustomBox } from "@/common/StyledComponents";
import { selectFirstName } from "@/auth/authSlice";
import { selectWelcomeTourOpen, setWelcomeTourOpen } from "@/nav/navSlice";
import AiLogo from "@/ai/AiLogo";

export default function WelcomeTour({}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useDispatch();

    const open = useSelector(selectWelcomeTourOpen);
    const firstName = useSelector(selectFirstName);

    const [page, setPage] = useState(0);
    const pages = getPages({ firstName: firstName, isMobile });
    const isFirstPage = page == 0;
    const isLastPage = pages.length - 1 == page;

    const changePage = (newPage) => {
        if (newPage >= pages.length) {
            dispatch(setWelcomeTourOpen(false));
        } else {
            setPage(newPage);
        }
    };

    const closeTour = () => {
        dispatch(setWelcomeTourOpen(false));
    };

    const conditionalStyles = isMobile
        ? {
              width: 320,
              padding: "24px 24px 16px 24px",
          }
        : {
              width: 600,
              padding: "48px 48px 32px 48px",
          };
    const buttonWidth = isMobile ? "100px" : "150px";

    return (
        <Modal open={open} onClose={closeTour}>
            <CustomBox
                sx={{
                    ...conditionalStyles,
                    background: theme.palette.background.default,
                    textAlign: "center",
                    maxHeight: "90vh",
                    overflowY: "auto",
                }}
            >
                {!isFirstPage && (
                    <IconButton
                        onClick={closeTour}
                        sx={{
                            position: "absolute",
                            top: "12px",
                            right: "12px",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                <img
                    width={108}
                    src={`/MakeOS-Logo-${theme.palette.mode}-theme.svg`}
                />

                <div>{pages[page]}</div>

                <Grid container alignItems="center">
                    <Grid item>
                        {isFirstPage ? (
                            <Button
                                variant="outlined"
                                color="info"
                                sx={{ width: buttonWidth }}
                                onClick={closeTour}
                            >
                                Skip Tour
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                color="info"
                                sx={{ width: buttonWidth }}
                                startIcon={<KeyboardArrowLeftIcon />}
                                onClick={() => changePage(page - 1)}
                            >
                                Previous
                            </Button>
                        )}
                    </Grid>

                    <Grid item flexGrow={1}>
                        {!isMobile && (
                            <Pagination>
                                {pages.map((_, i) => (
                                    <span
                                        key={i}
                                        className={i == page ? "active" : ""}
                                        onClick={() => changePage(i)}
                                    ></span>
                                ))}
                            </Pagination>
                        )}
                    </Grid>

                    <Grid item>
                        <Button
                            variant="contained"
                            sx={{ width: buttonWidth }}
                            endIcon={<KeyboardArrowRightIcon />}
                            onClick={() => changePage(page + 1)}
                        >
                            {isLastPage ? "End Tour" : "Next"}
                        </Button>
                    </Grid>
                </Grid>
            </CustomBox>
        </Modal>
    );
}

const Pagination = styled.div`
    width: 120px;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    margin: 0 auto;

    span {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background-color: #464646;
        cursor: pointer;

        &.active {
            background-color: #c9c9c9;
        }
    }
`;

const Page = styled.div`
    padding: 36px 0 48px 0;

    h1 {
        font-size: 22px;
        font-weight: 400;
        margin: 24px auto;

        img {
            display: inline-block;
            vertical-align: top;
            margin-right: 6px;
        }
    }

    p {
        font-size: 14px;
        margin: 24px;
    }
`;

function getPages({ firstName, isMobile }) {
    return [
        <Page>
            <h1>Welcome, {firstName} - Let us show you around</h1>
        </Page>,
        <Page>
            <h1>
                <AiLogo /> AI Quick Start
            </h1>
            <p>
                When you see this icon, that's AI Quick Start. It can do a lot
                of heavy lifting for you. Think of it like your virtual
                Production Assistant.
            </p>
        </Page>,
        <Page>
            <h1>
                <AiLogo /> AI Quick Start
            </h1>
            <p>
                Drop in decks, scripts, or Slack threads and get a structured
                project brief, budgets and milestones in minutes.
            </p>
            <img
                src={`/welcome_tour_ai_quick_start${isMobile ? "_mobile" : ""}.svg`}
                width="100%"
            />
        </Page>,
        <Page>
            <p>
                From there you can tweak the fine touches by editing project
                details and budgets, and easily build new budgets with reusable
                bid templates and version control.
            </p>
            <img
                src={`/welcome_tour_bid_sheet${isMobile ? "_mobile" : ""}.svg`}
                width="100%"
            />
        </Page>,
        <Page>
            <p>
                Feel like doing it all from scratch? You can do everything the
                old fashioned way too. Just click "Create" and you'll be up and
                running in no time.
            </p>
            <img src="/welcome_tour_create_button.svg" />
        </Page>,
        <Page>
            <h1>But we think it's better to learn by doing.</h1>
            <p>Let's make something!</p>
        </Page>,
    ];
}
